// CAMPO ÚNICO DE LOCALIZAÇÃO — "Onde?" (hero 10 / topo 8).
//
// Substitui o <select> de cidade por um combobox que encontra, na MESMA lista, bairro
// (desambiguado pela cidade: "Centro, Jundiaí" x "Centro (Jacaré), Cabreúva"), condomínio/
// empreendimento, cidade, tipo de imóvel e código. O filtro roda no cliente sobre o índice do
// tenant (utils/buscaLocal.ts), então a lista responde sem latência de rede.
//
// Acessibilidade: padrão ARIA combobox + listbox (role/aria-expanded/aria-activedescendant),
// navegação por ↑ ↓ Enter Esc e Home/End, e a lista some no blur. Os 4 estados de componente
// (default / carregando / vazio / erro) estão cobertos.
//
// Tema: TODA cor sai de variável CSS escopada no .module.css (que herda as --cor* do tenant) —
// nada de cor hardcoded, para funcionar nos dois temas.
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { filtraIndice, normalizaTermo, rotuloCompleto, totalDeItens } from '../../../utils/buscaLocal';
import type { GrupoResultado, Sugestao } from '../../../utils/buscaLocal';
import styles from './SmartLocationSearch.module.css';

/**
 * Ciclo de vida do índice que alimenta o campo. Quem passa uma lista pronta (o hero 8 monta a
 * dele a partir do `allCities` que já recebe) usa direto 'pronto'; os outros estados existem
 * para quem carregar o índice sob demanda.
 */
export type EstadoIndice = 'ocioso' | 'carregando' | 'pronto' | 'erro' | 'grande';

export interface SmartLocationSearchProps {
  indice: Sugestao[];
  estado: EstadoIndice;
  /** chamado no primeiro foco/digitação — dispara o carregamento lazy do índice. */
  onAtivar: () => void;
  /** texto exibido no input (controlado pelo hero, que também guarda a escolha). */
  valor: string;
  onChangeValor: (valor: string) => void;
  onEscolher: (sugestao: Sugestao) => void;
  /** limpa a escolha quando o usuário apaga/edita o texto. */
  onLimparEscolha: () => void;
  placeholder?: string;
  /** versão enxuta para o topo (altura menor, sem rótulo). */
  compacto?: boolean;
  className?: string;
  ariaLabel?: string;
  /**
   * Caracteres necessários para a lista abrir. `0` = abre completa já no foco, que é como o
   * hero 8 usa o campo de Cidade: ele SUBSTITUI um <select>, então precisa mostrar as opções
   * sem exigir digitação — quem quiser filtrar, digita.
   */
  minimoParaAbrir?: number;
  /** quantos itens no máximo por grupo (o padrão 6 é curto demais para uma lista de cidades). */
  limitePorGrupo?: number;
  /** desenha a seta de select (simetria com os selects nativos ao lado). */
  comSeta?: boolean;
  /**
   * Rótulo de uma primeira opção que LIMPA a escolha — o equivalente ao
   * `<option value="">Todas as cidades</option>` do select que este campo substitui.
   */
  opcaoVazia?: string;
  /** não sugere "código do imóvel" a partir do que foi digitado (campos de escolha fechada). */
  semCodigo?: boolean;
  /** chamado quando a pessoa sai do campo — quem usa decide o que fazer com texto solto. */
  onSair?: () => void;
}

const SmartLocationSearch: React.FC<SmartLocationSearchProps> = ({
  indice,
  estado,
  onAtivar,
  valor,
  onChangeValor,
  onEscolher,
  onLimparEscolha,
  placeholder = 'Bairro, condomínio, cidade ou código',
  compacto = false,
  className,
  ariaLabel = 'Onde você procura',
  minimoParaAbrir = 2,
  limitePorGrupo = 6,
  comSeta = false,
  opcaoVazia,
  semCodigo = false,
  onSair,
}) => {
  const reactId = useId();
  const idBase = `busca-local-${String(reactId).replace(/[^a-zA-Z0-9]/g, '')}`;
  const [aberto, setAberto] = useState(false);
  const [destaque, setDestaque] = useState(-1);
  const [abreParaCima, setAbreParaCima] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  // O portal precisa do document: no SSR/primeiro render ele não existe.
  const [montado, setMontado] = useState(false);
  useEffect(() => setMontado(true), []);
  const painelRef = useRef<HTMLDivElement>(null);
  // Posição do painel na JANELA. Ele é renderizado em portal no <body> (ver comentário no
  // JSX), então precisa das coordenadas — não herda mais o `position:relative` do campo.
  const [caixa, setCaixa] = useState<{ left: number; width: number; top: number; bottom: number } | null>(
    null
  );

  // Quem SUBSTITUI um <select> (minimoParaAbrir 0) tem quase sempre um texto no campo — a
  // escolha em vigor, e a Transação já nasce com uma ("Venda"). Filtrar por esse texto ao
  // reabrir devolvia UMA opção: a que já estava escolhida. Quem quisesse trocar para Aluguel
  // tinha que apagar o campo primeiro — um <select> nunca exigiu isso. Então o filtro só passa
  // a valer depois que a pessoa DIGITA; abrir pelo foco/clique mostra a lista inteira.
  const [digitou, setDigitou] = useState(false);
  const modoSelect = minimoParaAbrir === 0;
  const termoDoFiltro = modoSelect && !digitou ? '' : valor;

  const grupos: GrupoResultado[] = useMemo(
    () =>
      estado === 'pronto'
        ? filtraIndice(indice, termoDoFiltro, limitePorGrupo, minimoParaAbrir, !semCodigo)
        : [],
    [estado, indice, termoDoFiltro, limitePorGrupo, minimoParaAbrir, semCodigo]
  );

  // Lista achatada na ordem visual — é sobre ela que o teclado anda.
  const itens: Sugestao[] = useMemo(() => grupos.flatMap((g) => g.itens), [grupos]);
  const total = totalDeItens(grupos);

  useEffect(() => {
    if (!itens.length) {
      setDestaque(-1);
      return;
    }
    // Lista inteira aberta a partir de uma escolha existente: o teclado parte de onde a pessoa
    // está (como no select nativo), não do primeiro item — senão ↓ + Enter troca a escolha por
    // engano. Ao digitar, o destaque volta para o primeiro resultado.
    if (modoSelect && !digitou && valor.trim()) {
      const alvo = normalizaTermo(valor);
      const i = itens.findIndex((s) => normalizaTermo(s.label) === alvo);
      setDestaque(i >= 0 ? i : 0);
      return;
    }
    setDestaque(0);
  }, [itens, valor, digitou, modoSelect]);

  // Mantém o item destacado à vista dentro do painel: a lista rola (max-height) e, com a lista
  // inteira aberta, a escolha em vigor pode estar bem abaixo do topo.
  useEffect(() => {
    if (!aberto || destaque < 0) return;
    const opcao = document.getElementById(`${idBase}-opt-${destaque}`);
    const lista = document.getElementById(`${idBase}-lista`);
    if (!opcao || !lista) return;
    const o = opcao.getBoundingClientRect();
    const l = lista.getBoundingClientRect();
    if (o.top < l.top) lista.scrollTop -= l.top - o.top;
    else if (o.bottom > l.bottom) lista.scrollTop += o.bottom - l.bottom;
  }, [aberto, destaque, idBase]);

  // Mede o campo na janela: define ONDE o painel (portal, position:fixed) aparece e se ele
  // abre para cima — a busca do hero fica perto da dobra e, sempre para baixo, nasceria
  // cortada. Refaz a conta em scroll/resize para o painel não descolar do campo.
  useEffect(() => {
    if (!aberto || typeof window === 'undefined') return undefined;
    const medir = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (!r) return;
      setCaixa({ left: r.left, width: r.width, top: r.top, bottom: r.bottom });
      const abaixo = window.innerHeight - r.bottom;
      setAbreParaCima(abaixo < 320 && r.top > abaixo);
    };
    medir();
    window.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('scroll', medir, true);
      window.removeEventListener('resize', medir);
    };
  }, [aberto, itens.length]);

  // Clique fora fecha a lista (o blur do input sozinho não cobre o clique na própria opção).
  // O painel mora no <body>, então "dentro" é o campo OU o painel.
  useEffect(() => {
    if (!aberto) return undefined;
    const aoClicarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (wrapRef.current?.contains(alvo) || painelRef.current?.contains(alvo)) return;
      setAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [aberto]);

  const escolher = (sugestao: Sugestao) => {
    onEscolher(sugestao);
    onChangeValor(rotuloCompleto(sugestao));
    setAberto(false);
  };

  const aoDigitar = (texto: string) => {
    onChangeValor(texto);
    onLimparEscolha();
    setAberto(true);
    setDigitou(true);
    onAtivar();
  };

  const aoTeclar = (evento: React.KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === 'Escape') {
      setAberto(false);
      return;
    }
    if (evento.key === 'Enter') {
      // Só intercepta o Enter quando há uma opção destacada; senão deixa o form submeter.
      if (aberto && destaque >= 0 && itens[destaque]) {
        evento.preventDefault();
        escolher(itens[destaque]);
      }
      return;
    }
    if (!itens.length) return;

    if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      evento.preventDefault();
      setAberto(true);
      setDestaque((atual) => {
        const passo = evento.key === 'ArrowDown' ? 1 : -1;
        const proximo = atual + passo;
        if (proximo < 0) return itens.length - 1;
        if (proximo >= itens.length) return 0;
        return proximo;
      });
    } else if (evento.key === 'Home') {
      evento.preventDefault();
      setDestaque(0);
    } else if (evento.key === 'End') {
      evento.preventDefault();
      setDestaque(itens.length - 1);
    }
  };

  const mostraPainel =
    aberto &&
    (estado === 'carregando' || estado === 'erro' || valor.trim().length >= minimoParaAbrir);

  // índice global do item, para casar o destaque do teclado com o item renderizado
  let corrido = -1;

  // A <ul> vive numa variável porque é renderizada em PORTAL, longe daqui no DOM.
  const conteudoPainel = (
    <ul className={styles.lista} id={`${idBase}-lista`} role="listbox" aria-label={ariaLabel}>
            {opcaoVazia && estado === 'pronto' ? (
              <li
                // sem `opcaoAtiva` aqui: o destaque do teclado começa na primeira cidade e dois
                // itens realçados ao mesmo tempo confundem quem está escolhendo.
                className={`${styles.opcao} ${styles.opcaoVazia}`}
                role="option"
                aria-selected={!valor.trim()}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChangeValor('');
                  onLimparEscolha();
                  setAberto(false);
                }}
              >
                <span className={styles.opcaoTexto}>{opcaoVazia}</span>
              </li>
            ) : null}
            {estado === 'carregando' ? (
              <li className={styles.aviso} role="presentation">
                Carregando localidades…
              </li>
            ) : estado === 'erro' ? (
              <li className={styles.aviso} role="presentation">
                Não deu para carregar as sugestões. Escreva e aperte Buscar.
              </li>
            ) : total === 0 ? (
              <li className={styles.aviso} role="presentation">
                Nada encontrado para “{valor.trim()}”.
              </li>
            ) : (
              grupos.map((grupo) => (
                <li key={grupo.categoria} className={styles.grupo} role="presentation">
                  {/* com um grupo só (ex.: lista de cidades) o título é ruído: a lista já é
                      daquilo. Aparece quando há mais de uma categoria para separar. */}
                  {grupos.length > 1 ? (
                    <span className={styles.grupoTitulo}>{grupo.titulo}</span>
                  ) : null}
                  <ul className={styles.grupoLista} role="group" aria-label={grupo.titulo}>
                    {grupo.itens.map((item) => {
                      corrido += 1;
                      const indiceItem = corrido;
                      return (
                        <li
                          key={`${item.categoria}-${item.id}-${item.label}`}
                          id={`${idBase}-opt-${indiceItem}`}
                          role="option"
                          aria-selected={indiceItem === destaque}
                          className={`${styles.opcao} ${
                            indiceItem === destaque ? styles.opcaoAtiva : ''
                          }`}
                          // mousedown (não click): dispara antes do blur do input
                          onMouseDown={(e) => {
                            e.preventDefault();
                            escolher(item);
                          }}
                          onMouseEnter={() => setDestaque(indiceItem)}
                        >
                          <span className={styles.opcaoTexto}>
                            <strong className={styles.opcaoLabel}>{item.label}</strong>
                            {item.sublabel ? (
                              <span className={styles.opcaoSub}>, {item.sublabel}</span>
                            ) : null}
                          </span>
                          {item.qtd ? (
                            <span className={styles.opcaoQtd}>
                              {item.qtd} {item.qtd === 1 ? 'imóvel' : 'imóveis'}
                            </span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))
            )}
    </ul>
  );

  return (
    <div
      ref={wrapRef}
      className={`${styles.wrap} ${compacto ? styles.compacto : ''} ${className || ''}`}
    >
      <input
        id={idBase}
        className={styles.input}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-label={ariaLabel}
        aria-expanded={mostraPainel}
        aria-controls={`${idBase}-lista`}
        aria-autocomplete="list"
        aria-activedescendant={
          mostraPainel && destaque >= 0 ? `${idBase}-opt-${destaque}` : undefined
        }
        placeholder={placeholder}
        value={valor}
        onFocus={(e) => {
          onAtivar();
          setDigitou(false);
          // Texto todo selecionado no modo select: a primeira tecla TROCA a escolha em vez de
          // emendar nela ("Venda" + "a" = "Vendaa", que não casa com opção nenhuma).
          if (modoSelect && valor) e.currentTarget.select();
          if (valor.trim().length >= minimoParaAbrir) setAberto(true);
        }}
        onChange={(e) => aoDigitar(e.target.value)}
        // Reabre depois de um Esc sem exigir que a pessoa saia e volte ao campo.
        onClick={() => {
          if (valor.trim().length >= minimoParaAbrir) setAberto(true);
        }}
        // As opções são escolhidas no mousedown com preventDefault, então o input NÃO perde o
        // foco ao clicar numa delas: um blur aqui significa que a pessoa saiu do campo mesmo.
        onBlur={() => {
          setAberto(false);
          setDigitou(false);
          onSair?.();
        }}
        onKeyDown={aoTeclar}
      />

      {comSeta ? (
        // Fica sempre visível (não some quando há texto): ao lado de <select> nativos, um
        // campo que perde a seta ao ser preenchido destoa da linha inteira. Quem limpa é a
        // opção `opcaoVazia` no topo da lista, o equivalente ao <option value="">.
        <span className={styles.seta} aria-hidden="true" />
      ) : valor ? (
        <button
          type="button"
          className={styles.limpar}
          aria-label="Limpar localização"
          onClick={() => {
            onChangeValor('');
            onLimparEscolha();
            setAberto(false);
          }}
        >
          ×
        </button>
      ) : null}

      {/* PORTAL no <body>, com position:fixed. O painel NÃO pode morar dentro do hero: a linha
          de busca usa backdrop-filter, que cria stacking context e prende o z-index da lista
          ali dentro (ela saía POR BAIXO dos carrosséis da home), e a section do hero tem
          overflow:hidden, que ainda cortava o pé da lista. No <body> nada disso alcança. */}
      {montado && mostraPainel && caixa
        ? createPortal(
            <div
              ref={painelRef}
              className={`${styles.painel} ${styles.painelFlutuante} ${
                abreParaCima ? styles.painelAcima : ''
              } ${className || ''}`}
              style={{
                left: caixa.left - 14,
                width: caixa.width + 28,
                ...(abreParaCima
                  ? { bottom: window.innerHeight - caixa.top + 10 }
                  : { top: caixa.bottom + 10 }),
              }}
            >
              {conteudoPainel}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default SmartLocationSearch;
